import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../domains/auth/contexts/AuthContext';
import { hasFullPlatformStaffStatus } from '../domains/auth/teacherAccess';

const CLAIM_SUB_REDIRECT_KEY = 'og_post_login_path';

/**
 * Public landing route for Slack CTA links.
 *
 * Stores the intended in-app claim path in sessionStorage so teacher login can redirect
 * back into the app after auth.
 */
const ClaimSubLanding: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser, userData, loading } = useAuth();

  const eventId = params.get('eventId') || '';
  const squadId = params.get('squadId') || '';

  const inAppPath = useMemo(() => {
    const qs = new URLSearchParams();
    if (eventId) qs.set('eventId', eventId);
    if (squadId) qs.set('squadId', squadId);
    const s = qs.toString();
    return `/app/claim-sub${s ? `?${s}` : ''}`;
  }, [eventId, squadId]);

  useEffect(() => {
    // Persist redirect target for login
    try {
      if (eventId && squadId) {
        sessionStorage.setItem(CLAIM_SUB_REDIRECT_KEY, inAppPath);
      }
    } catch (_) {
      // ignore storage errors
    }

    // Terminal lifecycle statuses remain blocked by the callable.
    if (!loading && currentUser && userData?.role === 'teacher') {
      const staffStatus = userData.staffStatus ?? 'active';
      const isRestrictedTeacher = userData.isAdmin !== true && !hasFullPlatformStaffStatus(staffStatus);
      if (!isRestrictedTeacher && eventId && squadId) {
        navigate(inAppPath, { replace: true });
        return;
      }
    }

    // Otherwise send them to login.
    if (!loading) {
      navigate('/', { replace: true });
    }
  }, [currentUser, userData, loading, eventId, squadId, inAppPath, navigate]);

  return (
    <div style={{ padding: 24 }}>
      Redirecting…
    </div>
  );
};

export default ClaimSubLanding;
